import React from 'react';
import { Button, Card, Space, Tag, Typography, message } from 'antd';
import { Link } from 'react-router-dom';
import { ShareAltOutlined } from '@ant-design/icons';
import { queries } from '../../../graphql/graphql';
import PaginatedAdminList, { ListItem } from '../../../components/containers/PaginatedAdminList';
import { ProposalStatusTag } from '../../../components/Proposals/ProposalStatusTag';

const { Text, Title } = Typography;

interface ProposalListItem extends ListItem {
  orderId?: string;
  contractorId?: string;
  contractor?: {
    id?: string;
    name?: string;
  } | null;
  userId?: string;
  user?: {
    id?: string;
    name?: string;
    email?: string;
  } | null;
  planId?: string;
  type?: string;
  status?: string;
  mapResult?: {
    snapshot?: string | null;
    map?: {
      address?: string;
    } | null;
  } | null;
}

const AdminProposalCard: React.FC<{
  item: ListItem;
  refetch: () => void;
  prefix: string;
  roles?: string[];
}> = ({ item }) => {
  const proposal = item as ProposalListItem;
  const address = proposal.mapResult?.map?.address || 'Address pending';
  const snapshot = proposal.mapResult?.snapshot || null;
  const contractorName = proposal.contractor?.name || 'Unknown contractor';
  const userLabel =
    proposal.user?.name || proposal.user?.email || proposal.userId || 'Unknown user';
  const shareUrl = `${window.location.origin}/proposal/${proposal.id}`;
  const hasOrder = Boolean(proposal.orderId);
  const typeLabel = hasOrder ? 'Order' : 'Standalone';

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Proposal ${proposal.id.slice(-6).toUpperCase()}`,
          url: shareUrl,
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        message.success('Link copied to clipboard');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        message.error('Failed to copy link');
      }
    } else {
      message.error('Sharing not supported');
    }
  };

  const renderLink = (label: string, to?: string) => {
    if (!to) return <Text type="secondary">{label}</Text>;
    return <Link to={to}>{label}</Link>;
  };

  return (
    <Card
      hoverable
      actions={[
        <Link key={`view-${proposal.id}`} to={`/proposal/${proposal.id}`}>
          <Button type="link">View</Button>
        </Link>,
        <Link key={`edit-${proposal.id}`} to={`/admin/proposal/edit/${proposal.id}`}>
          <Button type="link">Edit</Button>
        </Link>,
        <Button
          key={`share-${proposal.id}`}
          type="link"
          icon={<ShareAltOutlined />}
          onClick={handleShare}>
          Share
        </Button>,
      ]}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {snapshot && (
          <div
            style={{
              width: '100%',
              height: 200,
              borderRadius: 12,
              overflow: 'hidden',
              backgroundColor: '#f5f5f5',
            }}>
            <img
              src={snapshot}
              alt="Map preview"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
        <Space direction="vertical" size={4}>
          <Title level={5} style={{ margin: 0 }}>
            Proposal {proposal.id.slice(-6).toUpperCase()}
          </Title>
          <Text type="secondary">{address}</Text>
        </Space>
        <Space size={12} wrap>
          {hasOrder ? (
            <ProposalStatusTag status={proposal.status as any} />
          ) : (
            <Tag color="blue">{typeLabel}</Tag>
          )}
        </Space>
        <Space direction="vertical" size={4}>
          <Text type="secondary">
            Contractor:{' '}
            {renderLink(
              contractorName,
              proposal.contractor?.id ? `/admin/contractor/${proposal.contractor.id}` : undefined
            )}
          </Text>
          {hasOrder && (
            <Text type="secondary">
              Customer:{' '}
              {renderLink(
                userLabel,
                proposal.user?.id ? `/admin/user/${proposal.user.id}` : undefined
              )}
            </Text>
          )}
          {hasOrder && (
            <Text type="secondary">
              Order:{' '}
              {renderLink(
                proposal.orderId || '—',
                proposal.orderId ? `/admin/order/${proposal.orderId}` : undefined
              )}
            </Text>
          )}
          {proposal.planId && (
            <Text type="secondary">
              Plan:{' '}
              {renderLink(
                proposal.planId,
                proposal.planId ? `/admin/plan/${proposal.planId}` : undefined
              )}
            </Text>
          )}
        </Space>
      </Space>
    </Card>
  );
};

const ListProposals: React.FC = () => {
  return (
    <PaginatedAdminList
      prefix="proposal"
      query={queries.proposal.PROPOSALS}
      inline={false}
      editable={false}
      canCreate={true}
      adminView={true}
      // createPath="/admin/proposals/new"
      CustomCard={AdminProposalCard}
      defaultPageSize={12}
    />
  );
};

export default ListProposals;
